/**
 * Toast Store
 * Manages toast notifications
 */

import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

export type ToastType = 'success' | 'error' | 'warning' | 'info'

export interface Toast {
  id: string
  type: ToastType
  title: string
  message?: string
  duration?: number
  dismissible?: boolean
  action?: {
    label: string
    handler: () => void
  }
  onDismiss?: () => void
  createdAt: number
}

const DEFAULT_DURATION = 5000 // 5 seconds

export const useToastStore = defineStore('toast', () => {
  // ================================
  // State
  // ================================

  const toasts = ref<Toast[]>([])
  const timers = ref<Map<string, ReturnType<typeof setTimeout>>>(new Map())

  // ================================
  // Getters
  // ================================

  const activeToasts = computed(() => toasts.value)
  const hasToasts = computed(() => toasts.value.length > 0)

  // ================================
  // Actions
  // ================================

  /**
   * Clave de deduplicación: mismo tipo + título + mensaje = la misma
   * notificación (p. ej. reintentos de un formulario que falla con el mismo
   * error de validación no deben apilar un toast idéntico por cada intento).
   */
  const toastKey = (t: Pick<Toast, 'type' | 'title' | 'message'>) =>
    `${t.type}::${t.title}::${t.message ?? ''}`

  /**
   * Add a toast notification
   */
  const addToast = (options: Omit<Toast, 'id' | 'createdAt'>) => {
    // Un éxito invalida los errores visibles anteriores: mostrar «éxito»
    // junto a errores obsoletos de intentos fallidos previos del mismo
    // flujo es feedback contradictorio para el usuario.
    if (options.type === 'success') {
      toasts.value.filter(t => t.type === 'error').map(t => t.id).forEach(removeToast)
    }

    const toast: Toast = {
      id: `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      ...options,
      duration: options.duration ?? DEFAULT_DURATION,
      dismissible: options.dismissible ?? true,
      createdAt: Date.now()
    }

    // Dedup: si ya hay una notificación idéntica visible, refrescarla en
    // su lugar (reinicia su temporizador) en vez de apilar un duplicado.
    const existing = toasts.value.find(t => toastKey(t) === toastKey(toast))
    if (existing) {
      const existingTimer = timers.value.get(existing.id)
      if (existingTimer) {
        clearTimeout(existingTimer)
        timers.value.delete(existing.id)
      }
      if (toast.duration && toast.duration > 0) {
        timers.value.set(existing.id, setTimeout(() => removeToast(existing.id), toast.duration))
      }
      existing.createdAt = toast.createdAt
      existing.message = toast.message
      return existing.id
    }

    // Add to array
    toasts.value.push(toast)

    // Set auto-dismiss timer if duration is specified
    if (toast.duration && toast.duration > 0) {
      const timer = setTimeout(() => {
        removeToast(toast.id)
      }, toast.duration)

      timers.value.set(toast.id, timer)
    }

    return toast.id
  }

  /**
   * Remove a toast
   */
  const removeToast = (id: string) => {
    const index = toasts.value.findIndex(t => t.id === id)
    if (index > -1) {
      const toast = toasts.value[index]

      // Clear timer if exists
      const timer = timers.value.get(id)
      if (timer) {
        clearTimeout(timer)
        timers.value.delete(id)
      }

      // Call onDismiss callback if provided
      toast.onDismiss?.()

      // Remove from array
      toasts.value.splice(index, 1)
    }
  }

  /**
   * Remove all toasts
   */
  const clearToasts = () => {
    // Clear all timers
    timers.value.forEach(timer => clearTimeout(timer))
    timers.value.clear()

    // Call all onDismiss callbacks
    toasts.value.forEach(toast => toast.onDismiss?.())

    // Clear array
    toasts.value = []
  }

  /**
   * Show success toast
   */
  const success = (title: string, options?: Partial<Omit<Toast, 'id' | 'type' | 'title' | 'createdAt'>>) => {
    return addToast({
      type: 'success',
      title,
      ...options
    })
  }

  /**
   * Show error toast
   */
  const error = (title: string, options?: Partial<Omit<Toast, 'id' | 'type' | 'title' | 'createdAt'>>) => {
    return addToast({
      type: 'error',
      title,
      duration: 0, // Don't auto-dismiss errors by default
      ...options
    })
  }

  /**
   * Show warning toast
   */
  const warning = (title: string, options?: Partial<Omit<Toast, 'id' | 'type' | 'title' | 'createdAt'>>) => {
    return addToast({
      type: 'warning',
      title,
      ...options
    })
  }

  /**
   * Show info toast
   */
  const info = (title: string, options?: Partial<Omit<Toast, 'id' | 'type' | 'title' | 'createdAt'>>) => {
    return addToast({
      type: 'info',
      title,
      ...options
    })
  }

  /**
   * Show promise toast (loading -> success/error)
   */
  const promise = async <T>(
    promise: Promise<T>,
    options: {
      loading: string
      success: string | ((data: T) => string)
      error: string | ((error: any) => string)
      /**
       * Carga que el usuario NO pidió — una pantalla que se monta sola al abrirla.
       * En ese caso no se anuncia ni el progreso ni el éxito: atribuirle a la
       * persona una acción que no tomó es un toast que miente, y como el store
       * de toasts es global sobrevive a la navegación y se acumula sobre la
       * pantalla siguiente.
       *
       * El éxito queda GATEADO por la bandera, no eliminado: la línea de más
       * abajo sigue pidiendo el toast, y sólo `silent` la saltea. El error
       * se anuncia SIEMPRE — un reporte que no carga y no lo dice es peor que
       * uno que avisa — y la excepción se propaga igual: silenciar el toast no
       * es tragarse el fallo.
       */
      silent?: boolean
    }
  ): Promise<T> => {
    // Show loading toast
    const loadingId = options.silent
      ? null
      : addToast({
        type: 'info',
        title: options.loading,
        duration: 0
      })

    try {
      const result = await promise

      // Remove loading toast
      if (loadingId) removeToast(loadingId)

      // Show success toast
      const successTitle = typeof options.success === 'function'
        ? options.success(result)
        : options.success

      if (options.silent) return result
      success(successTitle)

      return result
    } catch (err) {
      // Remove loading toast
      if (loadingId) removeToast(loadingId)

      // Show error toast
      const errorTitle = typeof options.error === 'function'
        ? options.error(err)
        : options.error

      error(errorTitle)

      throw err
    }
  }

  // ================================
  // Return store interface
  // ================================

  return {
    // State
    toasts: computed(() => toasts.value),

    // Getters
    activeToasts,
    hasToasts,

    // Actions
    addToast,
    removeToast,
    clearToasts,
    success,
    error,
    warning,
    info,
    promise
  }
})
